// src/pages/CoursesPage.js
import React, { useMemo } from 'react';
import { useNavigation } from '../contexts/NavigationContext';
import { useAuth } from '../contexts/AuthContext'; 
import { COURSE_UNITS_DATA } from '../data/unitsData';
import { 
    FaBookOpen, FaArrowRight, FaLock, FaGraduationCap, 
    FaChild, FaBriefcase, FaLaptopCode, FaPencilAlt, 
    FaSpellCheck, FaGlobe, FaCalculator
} from 'react-icons/fa';

const CoursesPage = () => {
    const { navigateTo } = useNavigation();
    const { user } = useAuth(); 

    // --- LOGIC TO PARSE PERMISSIONS ---
    const availableCourseKeys = useMemo(() => {
        if (!COURSE_UNITS_DATA) {
            console.error("COURSE_UNITS_DATA is undefined or null. Check unitsData.js exports.");
            return [];
        }

        const allKeys = Object.keys(COURSE_UNITS_DATA);

        // 1. TEACHERS / MASTERS: See everything always
        if (!user || user.role === 'teacher' || user.role === 'master' || user.hasFullAccess) {
            return allKeys;
        }

        // 2. STUDENTS: Filter based on allowedCourses
        let rawCourses = user.allowedCourses || user.enrolledCourses;
        let allowedIds = [];

        if (Array.isArray(rawCourses)) {
            allowedIds = rawCourses;
        } else if (typeof rawCourses === 'string') {
            allowedIds = rawCourses.split(',').map(s => s.trim());
        }

        // Return only the keys that the student is allowed to see
        return allKeys.filter(key => allowedIds.includes(key));
    }, [user]);

    // --- GROUP COURSES BY CATEGORY ---
    const groupedCourses = useMemo(() => {
        const groups = {};
        availableCourseKeys.forEach(key => {
            const course = COURSE_UNITS_DATA[key];
            if (course) {
                const cat = course.category || 'Other';
                if (!groups[cat]) groups[cat] = [];
                groups[cat].push({ id: key, ...course });
            }
        });
        return groups;
    }, [availableCourseKeys]);

    // --- CATEGORY ICONS ---
    const categoryIcons = {
        'Kids & Teens': <FaChild className="text-yellow-500" />,
        'General English': <FaGlobe className="text-blue-500" />,
        'Reading & Writing': <FaPencilAlt className="text-emerald-500" />,
        'Grammar & Drills': <FaSpellCheck className="text-purple-500" />,
        'Technology & Digital Skills': <FaLaptopCode className="text-cyan-500" />,
        'Business English': <FaBriefcase className="text-slate-500" />,
        'Exam Preparation': <FaGraduationCap className="text-red-500" />,
        'Numeracy & Everyday Math': <FaCalculator className="text-indigo-500" />
    };

    if (!user) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
                <FaLock className="text-6xl text-gray-300 mb-4" />
                <h2 className="text-2xl font-bold text-gray-700 mb-2">Access Restricted</h2>
                <p className="text-gray-500 max-w-md">Please log in to view your enrolled courses and training modules.</p>
                <button 
                    onClick={() => navigateTo('landing')}
                    className="mt-6 px-6 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition"
                >
                    Return to Login
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 animate-fade-in">
            <header className="mb-12 text-center md:text-left">
                <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-3">
                    Course Library
                </h1>
                <p className="text-lg text-gray-500 font-medium max-w-2xl">
                    Select a training module below to view its units and begin your lessons.
                </p>
            </header>

            {availableCourseKeys.length === 0 ? (
                <div className="bg-white p-12 rounded-2xl shadow-sm border border-gray-100 text-center">
                    <FaBookOpen className="text-6xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-700">No Courses Assigned</h3>
                    <p className="text-gray-500 mt-2">You are not currently enrolled in any active courses. Please contact your instructor.</p>
                </div>
            ) : (
                <div className="space-y-12">
                    {Object.entries(groupedCourses).map(([category, courses]) => {
                        const Icon = categoryIcons[category] || <FaBookOpen className="text-gray-400" />;
                        
                        return (
                            <div key={category} className="mb-10">
                                <div className="flex items-center gap-3 mb-6 pb-2 border-b-2 border-gray-100">
                                    <div className="text-2xl">{Icon}</div>
                                    <h2 className="text-2xl font-black text-gray-800 uppercase tracking-wide">{category}</h2>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {courses.map(course => (
                                        <div 
                                            key={course.id} 
                                            onClick={() => navigateTo('units', { courseId: course.id, title: course.title })}
                                            className="group bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 cursor-pointer overflow-hidden transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full"
                                        >
                                            {/* Course Image Header */}
                                            <div className="h-32 w-full overflow-hidden relative bg-gray-100">
                                                {course.image ? (
                                                    <img 
                                                        src={course.image} 
                                                        alt={course.title} 
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                                                        <FaBookOpen size={40} />
                                                    </div>
                                                )}
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                            </div>

                                            <div className="p-6 flex flex-col flex-grow relative">
                                                <div className="flex items-start justify-between gap-4 mb-3">
                                                    <h3 className="text-xl font-bold text-gray-800 leading-snug">{course.title}</h3>
                                                </div>
                                                <p className="text-sm text-gray-600 mb-6 flex-grow line-clamp-3 leading-relaxed">{course.description}</p>
                                                <div className="flex justify-between items-center mt-auto pt-4 border-t border-gray-100">
                                                    {user && user.role === 'student' ? (
                                                        <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">Unlocked</span>
                                                    ) : <span></span>}
                                                    <span className="text-indigo-600 text-sm font-bold flex items-center group-hover:translate-x-1 transition-transform">
                                                        Explore <FaArrowRight className="ml-1 text-xs" />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CoursesPage;